import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import RangeSlider from '../common/RangeSlider';
import { DELAY, REVERB, SEND_PLAYERS } from '../../utils/effectSends';

import { ReactComponent as AcousticIcon } from '../../icons/acoustic-guitar.svg';
import { ReactComponent as BrassIcon } from '../../icons/trumpet.svg';
import { ReactComponent as DrumIcon } from '../../icons/drum.svg';
import { ReactComponent as GuitarIcon } from '../../icons/electric-guitar.svg';
import { ReactComponent as MusicIcon } from '../../icons/music-double-note.svg';
import { ReactComponent as MelodyIcon } from '../../icons/music-single-note.svg';
import { ReactComponent as PadIcon } from '../../icons/pad-wave.svg';
import { ReactComponent as ViolinIcon } from '../../icons/violin.svg';

import './ChordBarsEffects.scss';

const formatPercent = (value) => `${Math.round(value * 100)}%`;

// Each player's icon, by SEND_PLAYERS id
const ICONS = {
  piano: MusicIcon,
  drums: DrumIcon,
  pad: PadIcon,
  brass: BrassIcon,
  guitar: GuitarIcon,
  acoustic: AcousticIcon,
  violin: ViolinIcon,
  melody: MelodyIcon,
};

// The players that share a level's name; the band's are named under `player.band`
const NAME_KEYS = {
  piano: 'player.instrument',
  drums: 'player.drums',
  pad: 'player.pad',
  melody: 'player.melody',
};

/**
 * The reverb and delay each player sends, a row per player heard in what plays. Shown in the
 * intensity panel's Effects card and in the chart player's mixer. Holds no state.
 *
 * @param {object} player - From useChordBarsPreview, with `effects` and `setEffect`
 * @param {string} idPrefix - Namespaces the sliders' ids
 * @param {boolean} drums - Whether the drums are among the players
 */
const ChordBarsEffects = ({ player, idPrefix, drums = false }) => {
  const { t } = useTranslation();
  const heard = {
    piano: true,
    drums: drums && player.drums,
    pad: player.hasPad,
    melody: player.hasMelody,
    ...Object.fromEntries((player.band || []).map((layer) => [layer.id, layer.has])),
  };

  const send = (id, name, bus, label) => (
    <RangeSlider
      id={`${idPrefix}-${id}-${bus}`}
      label={label}
      ariaLabel={`${name} ${label}`}
      value={player.effects[id]?.[bus] ?? 0}
      onChange={(event) => player.setEffect(id, bus, Number(event.target.value))}
      step={0.05}
      formatValue={formatPercent}
    />
  );

  return (
    <div className="ChordBarsEffects">
      {SEND_PLAYERS.filter(({ id }) => heard[id]).map(({ id, name: storedName, delay }) => {
        const Icon = ICONS[id];
        const name = t(NAME_KEYS[id] || `player.band.${id}`, { defaultValue: storedName });

        return (
          <div key={id} className="effect">
            <span className="effect-name">
              <Icon aria-hidden="true" />
              {name}
            </span>
            {/* A player with no delay gives its reverb the whole width */}
            <div className={cx('sends', { single: !delay })}>
              {send(id, name, REVERB, t('player.reverb'))}
              {delay && send(id, name, DELAY, t('player.delay'))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

ChordBarsEffects.propTypes = {
  player: PropTypes.shape({
    effects: PropTypes.object.isRequired,
    setEffect: PropTypes.func.isRequired,
    drums: PropTypes.bool,
    hasPad: PropTypes.bool,
    hasMelody: PropTypes.bool,
    band: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, has: PropTypes.bool })),
  }).isRequired,
  idPrefix: PropTypes.string.isRequired,
  drums: PropTypes.bool,
};

export default ChordBarsEffects;
